import React, { useEffect, useState } from 'react';
import { CheckCheck, Loader2, RefreshCcw, RotateCcw, Save, Send, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { HybridScoreCard } from './HybridScoreCard';
import { ChapterSampleInspector } from './ChapterSampleInspector';

export const ReviewWorkspace = ({ detail, editors, busy, onAssign, onNotes, onDecision, onRescore }) => {
  const review = detail?.review;
  const [notes, setNotes] = useState('');
  useEffect(() => { setNotes(review?.reviewer_notes || ''); }, [review?.id, review?.reviewer_notes]);

  if (!review) {
    return <section className="flex min-h-[34rem] items-center justify-center border border-border bg-card text-sm text-muted-foreground" data-testid="review-workspace-empty">Select a manuscript to begin review.</section>;
  }

  const resolved = review.status === 'resolved';
  return (
    <section className="space-y-4" data-testid="review-workspace">
      <header className="border border-border bg-card px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{review.author_name} · {review.genre}</div>
            <h2 className="mt-1 font-serif-novel text-2xl font-semibold leading-tight" data-testid="review-workspace-title">{review.title}</h2>
            <p className="mt-1 text-xs text-muted-foreground" data-testid="review-workspace-status">{review.chapter_count} chapters · {review.status?.replaceAll('_', ' ')}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onRescore} disabled={busy || review.ai_status === 'running'} data-testid="review-rescore-button">
            {review.ai_status === 'running' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />}
            Re-rank
          </Button>
        </div>
      </header>

      <HybridScoreCard review={review} />
      <ChapterSampleInspector samples={review.samples || []} />

      <section className="border border-border bg-card p-4" data-testid="editorial-action-panel">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
          <div>
            <label htmlFor="review-assignee-select" className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <UserRound className="h-3.5 w-3.5" /> Assigned editor
            </label>
            <select
              id="review-assignee-select"
              value={review.assigned_to || ''}
              onChange={(event) => onAssign(event.target.value || null)}
              disabled={busy}
              className="mt-2 h-9 w-full border border-border bg-background px-2 text-xs outline-none focus:ring-2 focus:ring-ring"
              data-testid="review-assignee-select"
            >
              <option value="">Unassigned</option>
              {editors.map((editor) => <option key={editor} value={editor}>{editor}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="review-notes-input" className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Reviewer notes</label>
            <Textarea
              id="review-notes-input"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Record context, chapter references, and the recommended next step…"
              className="mt-2 min-h-28 resize-y text-xs leading-relaxed"
              maxLength={12000}
              data-testid="review-notes-input"
            />
            <Button variant="outline" size="sm" onClick={() => onNotes(notes)} disabled={busy} className="mt-2" data-testid="review-notes-save-button">
              <Save className="mr-1.5 h-3.5 w-3.5" /> Save notes
            </Button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4">
          {resolved ? (
            <Button variant="outline" onClick={() => onDecision('reopen', notes)} disabled={busy} data-testid="review-reopen-button">
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reopen
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onDecision('mark_reviewed', notes)} disabled={busy} data-testid="review-mark-reviewed-button">
                <CheckCheck className="mr-1.5 h-3.5 w-3.5" /> Mark reviewed
              </Button>
              <Button variant="outline" onClick={() => onDecision('request_changes', notes)} disabled={busy || !notes.trim()} data-testid="review-request-changes-button">
                <Send className="mr-1.5 h-3.5 w-3.5" /> Request changes
              </Button>
              <Button onClick={() => onDecision('resolve', notes)} disabled={busy} data-testid="review-resolve-button">
                <CheckCheck className="mr-1.5 h-3.5 w-3.5" /> Resolve review
              </Button>
            </>
          )}
        </div>
      </section>

      <section className="border border-border bg-panel-header/15 p-4" data-testid="review-event-history">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Activity</div>
        {(detail.events || []).length === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">No review actions recorded yet.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {detail.events.slice(0, 8).map((event) => (
              <li key={event.id} className="flex justify-between gap-3 text-[10.5px]" data-testid={`review-event-${event.id}`}>
                <span><b>{event.actor}</b> · {event.kind?.replaceAll('_', ' ')}</span>
                <span className="shrink-0 text-muted-foreground">{new Date(event.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
};